'use client'

import { CloseButton, Transition } from '@headlessui/react'
import { MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { CarouselBottomArrows } from '@src/components/Carousel/CarouselBottomArrows'
import { useLocale } from '@src/i18n/LocaleProvider'

import type { CarouselHandlers } from '../ProductCarousel/ProductCarousel'
import { ProductCarousel } from '../ProductCarousel/ProductCarousel'
import pinchSvg from './assets/pinch.svg'

const scaleStep = 0.33

export const ProductGalleryModalContent = ({
  images,
  currentSlide,
  nextSlide,
  previousSlide,
  setCurrentSlide,
}: {
  images: {
    url: string
    alt: string
  }[]
  currentSlide: number
  nextSlide: () => void
  previousSlide: () => void
  setCurrentSlide: (value: number) => void
}) => {
  const [scale, setScale] = useState(1.0)
  const [overlayVisible, setOverlayVisible] = useState(true)
  const { t } = useLocale('product-page')
  const carouselRef = useRef<CarouselHandlers>(null)

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowRight') {
        nextSlide()
        carouselRef.current?.moveToIndex(currentSlide + 1)
      }
      if (event.key === 'ArrowLeft') {
        previousSlide()
        carouselRef.current?.moveToIndex(currentSlide - 1)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentSlide, nextSlide, previousSlide])

  const handleZoomIn = () => {
    setScale(Math.min(scale + scaleStep, 2.5))
  }

  const handleZoomOut = () => {
    setScale(Math.max(scale - scaleStep, 1.0))
  }

  return (
    <>
      <div className="relative max-w-full transition-transform lg:max-w-xl">
        <div
          className="transition-all duration-300"
          style={{
            transform: `scale(${scale.toFixed(1)})`,
          }}
        >
          <ProductCarousel
            handlersRef={carouselRef}
            onSlideChanged={setCurrentSlide}
            carouselClassName="gap-4"
            slidesPerView={1}
            initialSlide={currentSlide}
            slides={images.map((image, index) => (
              <div className="relative flex w-full justify-center" key={`${image.url}-${index}`}>
                <Image
                  src={image.url}
                  alt={image.alt}
                  width={662}
                  height={662}
                  className="pointer-events-none max-w-full flex-1 object-contain"
                />
              </div>
            ))}
          />
        </div>
      </div>
      <Transition show={overlayVisible}>
        <div
          className="absolute inset-0 left-1/2 top-1/2 flex size-36 -translate-x-1/2 -translate-y-1/2 touch-none flex-col items-center justify-center gap-2 rounded-2xl bg-bodi-black-text text-bodi-gray-butters opacity-95 transition-opacity duration-500 ease-out data-[leave]:opacity-0 lg:hidden "
          ref={(el) => {
            if (el) {
              setTimeout(() => {
                setOverlayVisible(false)
              }, 1500)
            }
          }}
        >
          <Image className="size-16" src={pinchSvg} alt="pinch gesture" width={64} height={64} />
          <p className="text-body-md">{t('pinch-to-zoom')}</p>
        </div>
      </Transition>
      <div className="absolute inset-x-0 bottom-4 flex justify-center">
        <CarouselBottomArrows
          desktopVisible
          mobileVisible
          nextSlide={() => carouselRef.current?.next()}
          previousSlide={() => carouselRef.current?.previous()}
        />
      </div>

      <div className="absolute right-4 top-4 flex flex-col gap-10 lg:right-20 lg:top-5">
        <CloseButton className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-bodi-black-text text-white shadow-md transition-colors hover:bg-bodi-gray-dark">
          <XMarkIcon className="size-6" />
        </CloseButton>

        <div className="hidden flex-col gap-2 lg:flex">
          <button
            onClick={handleZoomIn}
            className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-bodi-white text-bodi-black-text shadow-md transition-colors hover:bg-bodi-gray-pearl"
          >
            <PlusIcon className="size-6" />
          </button>
          <button
            onClick={handleZoomOut}
            className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-bodi-white text-bodi-black-text shadow-md transition-colors hover:bg-bodi-gray-pearl"
          >
            <MinusIcon className="size-6" />
          </button>
        </div>
      </div>
    </>
  )
}
